import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
    BookOpen, ChevronDown, Check, Search, Star, X, Loader2,
    Filter, BarChart3, Users, MousePointerClick, AlertCircle, DollarSign,
    TrendingUp, Compass, FolderTree as FolderTreeIcon, BookmarkPlus,
    CheckCircle2, Circle,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
    Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList,
} from '@/components/ui/command';
import { useToast } from '@/hooks/use-toast';
import {
    listLibraryTemplates,
    listFavoriteTemplateIds,
    addTemplateFavorite,
    removeTemplateFavorite,
    getProjects,
} from '@/services/api';
import type {
    TemplateLibraryItem, TemplateMetric, TemplatePeriod,
} from '@/types/template-library';
import MyTemplatesTab from './components/MyTemplatesTab';
import CloneToLibraryDialog from './components/CloneToLibraryDialog';
import BulkCloneToLibraryDialog from './components/BulkCloneToLibraryDialog';

type LibraryTab = 'explore' | 'mine';

const METRIC_OPTIONS: { value: TemplateMetric; label: string }[] = [
    { value: 'broadcasts', label: 'Envios' },
    { value: 'pageviews', label: 'Cliques' },
    { value: 'ctr', label: 'CTR' },
    { value: 'revenue', label: 'Receita' },
    { value: 'ecpm', label: 'eCPM' },
    { value: 'rpm', label: 'RPM' },
];

const PERIOD_OPTIONS: { value: TemplatePeriod; label: string }[] = [
    { value: '7d', label: '7d' },
    { value: '14d', label: '14d' },
    { value: '30d', label: '30d' },
    { value: '90d', label: '90d' },
    { value: 'all', label: 'Tudo' },
];

const LANGUAGES = [
    { code: '', label: 'Todos os idiomas' },
    { code: 'pt', label: 'Português' },
    { code: 'es', label: 'Espanhol' },
    { code: 'en', label: 'Inglês' },
    { code: 'fr', label: 'Francês' },
    { code: 'de', label: 'Alemão' },
    { code: 'it', label: 'Italiano' },
];

type TemplateType = 'any' | 'generic' | 'button';

const TYPE_LABELS: Record<TemplateType, string> = {
    any: 'Todos',
    generic: 'Card',
    button: 'Texto + botões',
};

const PER_PAGE = 24;

export default function TemplateLibraryPage() {
    const { toast } = useToast();
    const queryClient = useQueryClient();

    // Aba ativa: Explorar (catálogo template-intel) | Meus templates (próprios)
    const [tab, setTab] = useState<LibraryTab>('explore');

    // Template selecionado pra clonar pra biblioteca (dialog individual)
    const [cloneTarget, setCloneTarget] = useState<TemplateLibraryItem | null>(null);

    // Seleção em massa no Explorar → clonar vários.
    // Guarda os OBJETOS (não só ids) num Map pra persistir entre páginas:
    // ao paginar, os items da página anterior saem de `data.items`, então
    // precisamos manter os dados aqui pra conseguir clonar tudo no fim.
    const [selectedExplore, setSelectedExplore] = useState<Map<number, TemplateLibraryItem>>(new Map());
    const [bulkCloneOpen, setBulkCloneOpen] = useState(false);

    // ─── Filters state ─────────────────────────────────────────────
    const [searchInput, setSearchInput] = useState('');
    const [debouncedQ, setDebouncedQ] = useState('');
    const [projectFilter, setProjectFilter] = useState<number | null>(null);
    const [language, setLanguage] = useState<string>('');
    const [templateType, setTemplateType] = useState<TemplateType>('any');
    const [onlyFavorites, setOnlyFavorites] = useState(() => {
        return localStorage.getItem('template-library-only-favs') === '1';
    });
    const [metric, setMetric] = useState<TemplateMetric>(() => {
        return (localStorage.getItem('template-library-metric') as TemplateMetric) || 'broadcasts';
    });
    const [period, setPeriod] = useState<TemplatePeriod>(() => {
        return (localStorage.getItem('template-library-period') as TemplatePeriod) || '30d';
    });
    const [page, setPage] = useState(1);

    // Popover open states
    const [projectPopOpen, setProjectPopOpen] = useState(false);
    const [langPopOpen, setLangPopOpen] = useState(false);
    const [metricPopOpen, setMetricPopOpen] = useState(false);

    // Debounce search (400ms)
    useEffect(() => {
        const h = setTimeout(() => setDebouncedQ(searchInput.trim()), 400);
        return () => clearTimeout(h);
    }, [searchInput]);

    // Reset page quando filtro muda
    useEffect(() => {
        setPage(1);
    }, [debouncedQ, projectFilter, language, templateType, onlyFavorites, metric, period]);

    // Limpa seleção em massa ao trocar filtro (NÃO ao paginar — seleção
    // persiste entre páginas).
    useEffect(() => {
        setSelectedExplore(new Map());
    }, [debouncedQ, projectFilter, language, templateType, onlyFavorites, metric, period]);

    // Persiste preferências
    useEffect(() => {
        localStorage.setItem('template-library-only-favs', onlyFavorites ? '1' : '0');
    }, [onlyFavorites]);
    useEffect(() => {
        localStorage.setItem('template-library-metric', metric);
    }, [metric]);
    useEffect(() => {
        localStorage.setItem('template-library-period', period);
    }, [period]);

    // ─── Queries ───────────────────────────────────────────────────
    const { data: projects = [] } = useQuery({
        queryKey: ['template-library-projects'],
        queryFn: getProjects,
        staleTime: 1000 * 60 * 5,
    });

    const { data, isLoading, isFetching } = useQuery({
        queryKey: [
            'template-library', debouncedQ, projectFilter, language,
            templateType, onlyFavorites, metric, period, page,
        ],
        queryFn: () => listLibraryTemplates({
            q: debouncedQ || undefined,
            project_id: projectFilter ?? undefined,
            language: language || undefined,
            template_type: templateType,
            only_favorites: onlyFavorites,
            metric,
            period,
            page,
            per_page: PER_PAGE,
        }),
        staleTime: 1000 * 30,
        enabled: tab === 'explore',
    });

    const { data: favoriteIdsResp } = useQuery({
        queryKey: ['template-library-favorites'],
        queryFn: listFavoriteTemplateIds,
        staleTime: 1000 * 60 * 5,
    });

    const favoriteIds = useMemo(
        () => new Set(favoriteIdsResp?.template_ids ?? []),
        [favoriteIdsResp],
    );

    // ─── Toggle favorite com optimistic update ─────────────────────
    const toggleFavMutation = useMutation({
        mutationFn: async ({ id, currentlyFav }: { id: number; currentlyFav: boolean }) => {
            return currentlyFav
                ? removeTemplateFavorite(id)
                : addTemplateFavorite(id);
        },
        onMutate: async ({ id, currentlyFav }) => {
            // Optimistic update do cache de IDs
            await queryClient.cancelQueries({ queryKey: ['template-library-favorites'] });
            const prev = queryClient.getQueryData<{ template_ids: number[] }>(
                ['template-library-favorites'],
            );
            queryClient.setQueryData(['template-library-favorites'], (old: any) => {
                const ids = new Set((old?.template_ids ?? []) as number[]);
                if (currentlyFav) ids.delete(id);
                else ids.add(id);
                return { template_ids: Array.from(ids) };
            });
            return { prev };
        },
        onError: (_e, _v, ctx) => {
            if (ctx?.prev) {
                queryClient.setQueryData(['template-library-favorites'], ctx.prev);
            }
            toast({ title: 'Falha ao atualizar favorito', variant: 'destructive' });
        },
        onSettled: () => {
            // Se estamos no modo "só favoritos", invalida a lista pra refletir
            if (onlyFavorites) {
                queryClient.invalidateQueries({ queryKey: ['template-library'] });
            }
        },
    });

    const handleToggleFavorite = (template: TemplateLibraryItem) => {
        const id = template.template_id;
        const currentlyFav = favoriteIds.has(id);
        toggleFavMutation.mutate({ id, currentlyFav });
    };

    // ─── Computed ──────────────────────────────────────────────────
    const items = data?.items ?? [];
    const total = data?.total ?? 0;
    const totalPages = data?.pages ?? 1;

    // Seleção em massa (Explorar) — opera sobre o Map de objetos
    const toggleSelectExplore = (t: TemplateLibraryItem) => {
        setSelectedExplore((prev) => {
            const next = new Map(prev);
            if (next.has(t.template_id)) next.delete(t.template_id);
            else next.set(t.template_id, t);
            return next;
        });
    };
    // "Selecionar todos" age só na página atual, preservando seleções de outras
    const allExploreSelected = items.length > 0 && items.every((t) => selectedExplore.has(t.template_id));
    const toggleSelectAllExplore = () => {
        setSelectedExplore((prev) => {
            const next = new Map(prev);
            if (allExploreSelected) {
                items.forEach((t) => next.delete(t.template_id));
            } else {
                items.forEach((t) => next.set(t.template_id, t));
            }
            return next;
        });
    };
    const selectedExploreTemplates = Array.from(selectedExplore.values());

    const hasActiveFilters = !!(
        debouncedQ || projectFilter !== null || language || templateType !== 'any' || onlyFavorites
    );

    const clearFilters = () => {
        setSearchInput('');
        setDebouncedQ('');
        setProjectFilter(null);
        setLanguage('');
        setTemplateType('any');
        setOnlyFavorites(false);
    };

    const projectName = projects.find((p) => p.id === projectFilter)?.name;
    const languageLabel = LANGUAGES.find((l) => l.code === language)?.label;

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-violet-100 flex items-center justify-center">
                    <BookOpen className="w-5 h-5 text-violet-600" />
                </div>
                <div className="flex-1">
                    <h1 className="text-2xl font-bold tracking-tight">Biblioteca de modelos</h1>
                    <p className="text-sm text-gray-500">
                        Explore o catálogo ou gerencie seus próprios templates em pastas.
                    </p>
                </div>
            </div>

            {/* Abas */}
            <div className="border-b flex gap-1">
                {[
                    { id: 'explore' as LibraryTab, label: 'Explorar', icon: Compass },
                    { id: 'mine' as LibraryTab, label: 'Meus templates', icon: FolderTreeIcon },
                ].map((t) => (
                    <button
                        key={t.id}
                        type="button"
                        onClick={() => setTab(t.id)}
                        className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px flex items-center gap-1.5 transition-colors ${
                            tab === t.id
                                ? 'border-violet-600 text-violet-700'
                                : 'border-transparent text-gray-500 hover:text-gray-700'
                        }`}
                    >
                        <t.icon className="w-4 h-4" />
                        {t.label}
                    </button>
                ))}
            </div>

            {tab === 'mine' ? (
                <MyTemplatesTab />
            ) : (
            <>
            {/* Filtros */}
            <div className="bg-white border rounded-xl p-3 space-y-3 sticky top-0 z-10">
                <div className="flex flex-wrap gap-2 items-center">
                    {/* Search */}
                    <div className="relative flex-1 min-w-[200px]">
                        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                        <Input
                            value={searchInput}
                            onChange={(e) => setSearchInput(e.target.value)}
                            placeholder="Buscar título, subtítulo, texto..."
                            className="pl-8 h-9"
                        />
                        {searchInput && (
                            <button
                                type="button"
                                onClick={() => setSearchInput('')}
                                className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                                aria-label="Limpar busca"
                            >
                                <X className="w-3.5 h-3.5" />
                            </button>
                        )}
                    </div>

                    {/* Project filter */}
                    <Popover open={projectPopOpen} onOpenChange={setProjectPopOpen}>
                        <PopoverTrigger asChild>
                            <Button
                                variant="outline"
                                size="sm"
                                role="combobox"
                                className="h-9 justify-between font-normal min-w-[140px]"
                            >
                                <span className="truncate">
                                    {projectName ?? 'Projeto'}
                                </span>
                                <ChevronDown className="ml-2 h-3.5 w-3.5 shrink-0 opacity-50" />
                            </Button>
                        </PopoverTrigger>
                        <PopoverContent className="p-0 w-[280px]" align="start">
                            <Command>
                                <CommandInput placeholder="Buscar projeto..." />
                                <CommandList>
                                    <CommandEmpty>Nenhum projeto.</CommandEmpty>
                                    <CommandGroup>
                                        <CommandItem
                                            value="__all__"
                                            onSelect={() => {
                                                setProjectFilter(null);
                                                setProjectPopOpen(false);
                                            }}
                                        >
                                            <div className={`mr-2 flex h-4 w-4 items-center justify-center rounded-sm border ${
                                                projectFilter === null ? 'bg-violet-600 border-violet-600' : 'border-gray-300'
                                            }`}>
                                                {projectFilter === null && <Check className="h-3 w-3 text-white" />}
                                            </div>
                                            Todos os projetos
                                        </CommandItem>
                                        {projects.map((p) => (
                                            <CommandItem
                                                key={p.id}
                                                value={p.name}
                                                onSelect={() => {
                                                    setProjectFilter(p.id);
                                                    setProjectPopOpen(false);
                                                }}
                                            >
                                                <div className={`mr-2 flex h-4 w-4 items-center justify-center rounded-sm border ${
                                                    projectFilter === p.id ? 'bg-violet-600 border-violet-600' : 'border-gray-300'
                                                }`}>
                                                    {projectFilter === p.id && <Check className="h-3 w-3 text-white" />}
                                                </div>
                                                {p.name}
                                            </CommandItem>
                                        ))}
                                    </CommandGroup>
                                </CommandList>
                            </Command>
                        </PopoverContent>
                    </Popover>

                    {/* Language */}
                    <Popover open={langPopOpen} onOpenChange={setLangPopOpen}>
                        <PopoverTrigger asChild>
                            <Button
                                variant="outline"
                                size="sm"
                                className="h-9 justify-between font-normal min-w-[120px]"
                            >
                                <span className="truncate">{languageLabel ?? 'Idioma'}</span>
                                <ChevronDown className="ml-2 h-3.5 w-3.5 shrink-0 opacity-50" />
                            </Button>
                        </PopoverTrigger>
                        <PopoverContent className="p-0 w-[220px]" align="start">
                            <Command>
                                <CommandList>
                                    <CommandGroup>
                                        {LANGUAGES.map((l) => (
                                            <CommandItem
                                                key={l.code || '_'}
                                                value={l.label}
                                                onSelect={() => {
                                                    setLanguage(l.code);
                                                    setLangPopOpen(false);
                                                }}
                                            >
                                                <div className={`mr-2 flex h-4 w-4 items-center justify-center rounded-sm border ${
                                                    language === l.code ? 'bg-violet-600 border-violet-600' : 'border-gray-300'
                                                }`}>
                                                    {language === l.code && <Check className="h-3 w-3 text-white" />}
                                                </div>
                                                {l.label}
                                            </CommandItem>
                                        ))}
                                    </CommandGroup>
                                </CommandList>
                            </Command>
                        </PopoverContent>
                    </Popover>

                    {/* Ordenar por (metric) */}
                    <Popover open={metricPopOpen} onOpenChange={setMetricPopOpen}>
                        <PopoverTrigger asChild>
                            <Button
                                variant="outline"
                                size="sm"
                                className="h-9 justify-between font-normal min-w-[160px]"
                            >
                                <span className="flex items-center gap-1.5 truncate">
                                    <TrendingUp className="w-3.5 h-3.5 opacity-60" />
                                    {METRIC_OPTIONS.find((m) => m.value === metric)?.label || 'Ordenar'}
                                    <span className="text-gray-400">·</span>
                                    <span className="text-gray-500">
                                        {PERIOD_OPTIONS.find((p) => p.value === period)?.label ?? period}
                                    </span>
                                </span>
                                <ChevronDown className="ml-1 h-3.5 w-3.5 shrink-0 opacity-50" />
                            </Button>
                        </PopoverTrigger>
                        <PopoverContent className="p-2 w-[220px]" align="start">
                            <div className="space-y-3">
                                <div>
                                    <div className="text-[10px] uppercase font-medium text-gray-500 px-1.5 mb-1">
                                        Métrica
                                    </div>
                                    <div className="grid grid-cols-2 gap-1">
                                        {METRIC_OPTIONS.map((m) => (
                                            <button
                                                key={m.value}
                                                type="button"
                                                onClick={() => setMetric(m.value)}
                                                className={`px-2 py-1.5 text-xs rounded ${
                                                    metric === m.value
                                                        ? 'bg-violet-600 text-white'
                                                        : 'text-gray-700 hover:bg-gray-100'
                                                }`}
                                            >
                                                {m.label}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                                <div>
                                    <div className="text-[10px] uppercase font-medium text-gray-500 px-1.5 mb-1">
                                        Período
                                    </div>
                                    <div className="grid grid-cols-5 gap-1">
                                        {PERIOD_OPTIONS.map((p) => (
                                            <button
                                                key={p.value}
                                                type="button"
                                                onClick={() => setPeriod(p.value)}
                                                className={`px-1 py-1.5 text-xs rounded ${
                                                    period === p.value
                                                        ? 'bg-violet-600 text-white'
                                                        : 'text-gray-700 hover:bg-gray-100'
                                                }`}
                                            >
                                                {p.label}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </PopoverContent>
                    </Popover>

                    {/* Template type segmented */}
                    <div className="inline-flex border rounded-md h-9 overflow-hidden">
                        {(['any', 'generic', 'button'] as TemplateType[]).map((t, i) => (
                            <button
                                key={t}
                                type="button"
                                onClick={() => setTemplateType(t)}
                                className={`px-3 text-xs transition-colors ${
                                    templateType === t
                                        ? 'bg-violet-600 text-white'
                                        : 'bg-white text-gray-600 hover:bg-gray-50'
                                } ${i > 0 ? 'border-l' : ''}`}
                            >
                                {TYPE_LABELS[t]}
                            </button>
                        ))}
                    </div>

                    {/* Only favorites */}
                    <Button
                        variant={onlyFavorites ? 'default' : 'outline'}
                        size="sm"
                        onClick={() => setOnlyFavorites((v) => !v)}
                        className={onlyFavorites ? 'h-9 bg-amber-500 hover:bg-amber-600 text-white' : 'h-9'}
                    >
                        <Star className={`w-3.5 h-3.5 mr-1.5 ${onlyFavorites ? 'fill-current' : ''}`} />
                        {onlyFavorites ? 'Favoritos' : 'Favoritos'}
                    </Button>

                    {/* Clear filters */}
                    {hasActiveFilters && (
                        <Button
                            variant="ghost"
                            size="sm"
                            onClick={clearFilters}
                            className="h-9 text-gray-500"
                        >
                            <X className="w-3.5 h-3.5 mr-1" /> Limpar
                        </Button>
                    )}

                    {/* Total badge */}
                    <div className="ml-auto text-xs text-gray-500 flex items-center gap-1.5">
                        {isFetching && <Loader2 className="w-3 h-3 animate-spin" />}
                        <Filter className="w-3 h-3" />
                        {total.toLocaleString('pt-BR')} {total === 1 ? 'modelo' : 'modelos'}
                    </div>
                </div>
            </div>

            {/* Barra "selecionar todos" (a barra de ações é fixa no rodapé) */}
            {items.length > 0 && (
                <div className="flex items-center gap-3 px-1">
                    <label className="flex items-center gap-1.5 text-xs text-gray-500 cursor-pointer">
                        <Checkbox checked={allExploreSelected} onCheckedChange={toggleSelectAllExplore} />
                        Selecionar todos (página)
                    </label>
                    <span className="text-xs text-gray-400">
                        Clique num modelo pra selecionar.
                    </span>
                </div>
            )}

            {/* Body */}
            {isLoading ? (
                <SkeletonGrid />
            ) : items.length === 0 ? (
                <EmptyState
                    isFavOnly={onlyFavorites}
                    hasFilters={hasActiveFilters}
                    onClear={clearFilters}
                />
            ) : (
                <>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                        {items.map((t) => (
                            <TemplateCard
                                key={t.template_id}
                                template={t}
                                isFavorite={favoriteIds.has(t.template_id)}
                                selected={selectedExplore.has(t.template_id)}
                                onToggleSelect={() => toggleSelectExplore(t)}
                                onToggleFavorite={() => handleToggleFavorite(t)}
                                onSaveToLibrary={() => setCloneTarget(t)}
                            />
                        ))}
                    </div>

                    {/* Pagination simples */}
                    {totalPages > 1 && (
                        <div className="flex items-center justify-center gap-2 pt-2">
                            <Button
                                variant="outline"
                                size="sm"
                                disabled={page <= 1 || isFetching}
                                onClick={() => setPage((p) => Math.max(1, p - 1))}
                            >
                                Anterior
                            </Button>
                            <span className="text-sm text-gray-600 px-2">
                                {page} / {totalPages}
                            </span>
                            <Button
                                variant="outline"
                                size="sm"
                                disabled={page >= totalPages || isFetching}
                                onClick={() => setPage((p) => p + 1)}
                            >
                                Próximo
                            </Button>
                        </div>
                    )}
                </>
            )}
            </>
            )}

            {/* Barra de ações FIXA no rodapé (visível ao rolar) */}
            {tab === 'explore' && selectedExplore.size > 0 && (
                <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 flex items-center gap-3 bg-white border shadow-xl rounded-full pl-5 pr-2 py-2">
                    <span className="text-sm font-semibold text-violet-700">
                        {selectedExplore.size} selecionado{selectedExplore.size > 1 ? 's' : ''}
                    </span>
                    <Button size="sm" onClick={() => setBulkCloneOpen(true)} className="rounded-full">
                        <BookmarkPlus className="w-4 h-4 mr-1.5" /> Salvar na biblioteca
                    </Button>
                    <Button
                        size="sm" variant="ghost"
                        onClick={() => setSelectedExplore(new Map())}
                        className="rounded-full"
                    >
                        <X className="w-3.5 h-3.5 mr-1" /> Limpar
                    </Button>
                </div>
            )}

            {/* Dialog clonar 1 template do catálogo pra biblioteca própria */}
            <CloneToLibraryDialog
                template={cloneTarget}
                open={!!cloneTarget}
                onOpenChange={(o) => { if (!o) setCloneTarget(null); }}
            />

            {/* Dialog clonar VÁRIOS de uma vez */}
            <BulkCloneToLibraryDialog
                open={bulkCloneOpen}
                onOpenChange={setBulkCloneOpen}
                templates={selectedExploreTemplates}
                onSaved={() => setSelectedExplore(new Map())}
            />
        </div>
    );
}

// ─── TemplateCard ──────────────────────────────────────────────────

function TemplateCard({
    template, isFavorite, selected, onToggleSelect, onToggleFavorite, onSaveToLibrary,
}: {
    template: TemplateLibraryItem;
    isFavorite: boolean;
    selected: boolean;
    onToggleSelect: () => void;
    onToggleFavorite: () => void;
    onSaveToLibrary: () => void;
}) {
    const isCard = template.template_type === 'generic';
    const buttons = (template.buttons_json ?? []).slice(0, 3);

    return (
        <article
            onClick={onToggleSelect}
            className={`group bg-gradient-to-b from-gray-50 to-gray-100 border rounded-xl overflow-hidden hover:shadow-md transition-all flex flex-col cursor-pointer ${
                selected ? 'border-violet-400 ring-2 ring-violet-300' : 'hover:border-violet-300'
            }`}
        >
            {/* Header com badges + star */}
            <div className="flex items-center justify-between px-3 py-2 border-b bg-white/60 backdrop-blur-sm">
                <div className="flex items-center gap-1.5 min-w-0">
                    {selected
                        ? <CheckCircle2 className="w-4 h-4 text-violet-600 shrink-0" />
                        : <Circle className="w-4 h-4 text-gray-300 shrink-0 group-hover:text-violet-300" />}
                    <Badge variant="outline" className="text-[10px] shrink-0">
                        {isCard ? 'Card' : 'Texto + botões'}
                    </Badge>
                    {template.language && (
                        <Badge variant="outline" className="text-[9px] uppercase px-1.5 py-0">
                            {template.language}
                        </Badge>
                    )}
                    {template.domain && (
                        <span className="text-[10px] font-mono text-gray-500 truncate">
                            {template.domain}
                        </span>
                    )}
                </div>
                <div className="flex items-center gap-0.5 shrink-0">
                    <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); onSaveToLibrary(); }}
                        className="w-7 h-7 rounded-full hover:bg-violet-50 flex items-center justify-center transition-colors"
                        aria-label="Salvar na minha biblioteca"
                        title="Salvar na minha biblioteca (clonar)"
                    >
                        <BookmarkPlus className="w-4 h-4 text-gray-400 hover:text-violet-600" />
                    </button>
                    <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); onToggleFavorite(); }}
                        className="w-7 h-7 rounded-full hover:bg-gray-100 flex items-center justify-center transition-colors"
                        aria-label={isFavorite ? 'Remover dos favoritos' : 'Favoritar'}
                    >
                        <Star
                            className={`w-4 h-4 ${
                                isFavorite ? 'fill-amber-400 text-amber-400' : 'text-gray-400'
                            }`}
                        />
                    </button>
                </div>
            </div>

            {/* Preview Messenger-like */}
            <div className="p-3 flex-1 flex flex-col">
                <div className="bg-white rounded-2xl rounded-tl-sm shadow-sm border border-gray-200 overflow-hidden max-w-[300px]">
                    {/* Card template: imagem + título + subtitle + botões */}
                    {isCard ? (
                        <>
                            {template.image_url ? (
                                <div className="aspect-[1.91/1] bg-gray-100 overflow-hidden">
                                    <img
                                        src={template.image_url}
                                        alt=""
                                        loading="lazy"
                                        className="w-full h-full object-contain"
                                    />
                                </div>
                            ) : (
                                <div className="aspect-[1.91/1] bg-gradient-to-br from-violet-50 to-sky-50 flex items-center justify-center">
                                    <BookOpen className="w-8 h-8 text-gray-300" />
                                </div>
                            )}
                            <div className="p-2.5 space-y-0.5">
                                {template.title && (
                                    <p className="text-[13px] font-semibold text-gray-900 line-clamp-2 leading-tight">
                                        {template.title}
                                    </p>
                                )}
                                {template.subtitle && (
                                    <p className="text-[11px] text-gray-600 line-clamp-2 leading-tight">
                                        {template.subtitle}
                                    </p>
                                )}
                            </div>
                            {buttons.length > 0 && (
                                <div className="border-t">
                                    {buttons.map((b, i) => (
                                        <div
                                            key={i}
                                            className={`px-3 py-2 text-center text-[12px] text-violet-600 font-medium truncate ${
                                                i > 0 ? 'border-t border-gray-100' : ''
                                            }`}
                                        >
                                            {b.title || '(sem texto)'}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </>
                    ) : (
                        // Button template: texto + botões
                        <>
                            <div className="p-2.5">
                                <p className="text-[13px] text-gray-900 leading-snug whitespace-pre-line line-clamp-5">
                                    {template.text_content || template.title || '(sem texto)'}
                                </p>
                            </div>
                            {buttons.length > 0 && (
                                <div className="border-t">
                                    {buttons.map((b, i) => (
                                        <div
                                            key={i}
                                            className={`px-3 py-2 text-center text-[12px] text-violet-600 font-medium truncate ${
                                                i > 0 ? 'border-t border-gray-100' : ''
                                            }`}
                                        >
                                            {b.title || '(sem texto)'}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>

            {/* Footer com métricas (sempre mostra mesmo se zeradas — usuário entende que não tem dados) */}
            <div className="px-3 py-2 border-t bg-white/60 backdrop-blur-sm">
                <MetricsRow template={template} />
            </div>
        </article>
    );
}

function MetricsRow({ template }: { template: TemplateLibraryItem }) {
    const items: { icon: any; value: string; title: string; color: string }[] = [];

    if (template.broadcast_ctr != null && template.broadcast_ctr > 0) {
        items.push({
            icon: BarChart3,
            value: `${template.broadcast_ctr.toFixed(2)}%`,
            title: 'CTR (cliques / envios)',
            color: 'text-violet-600',
        });
    }
    if (template.total_sent != null && template.total_sent > 0) {
        items.push({
            icon: Users,
            value: formatCompact(template.total_sent),
            title: 'Leads alcançados',
            color: 'text-emerald-600',
        });
    }
    if (template.pageviews != null && template.pageviews > 0) {
        items.push({
            icon: MousePointerClick,
            value: formatCompact(template.pageviews),
            title: 'Cliques',
            color: 'text-sky-600',
        });
    }
    if (template.gam_revenue != null && template.gam_revenue > 0) {
        items.push({
            icon: DollarSign,
            value: formatMoney(template.gam_revenue),
            title: 'Receita',
            color: 'text-amber-600',
        });
    }
    if (template.gam_ecpm != null && template.gam_ecpm > 0) {
        items.push({
            icon: TrendingUp,
            value: formatMoney(template.gam_ecpm),
            title: 'eCPM',
            color: 'text-rose-600',
        });
    }

    if (items.length === 0) {
        return (
            <p className="text-[10px] text-gray-400">Sem métricas no período selecionado.</p>
        );
    }

    return (
        <div className="flex items-center gap-3 flex-wrap text-[11px] text-gray-700">
            {items.map((m, i) => (
                <span
                    key={i}
                    className="flex items-center gap-1"
                    title={m.title}
                >
                    <m.icon className={`w-3 h-3 ${m.color}`} />
                    <strong className="font-semibold">{m.value}</strong>
                </span>
            ))}
        </div>
    );
}

// ─── SkeletonGrid ──────────────────────────────────────────────────

function SkeletonGrid() {
    return (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="bg-white border rounded-xl overflow-hidden">
                    <div className="aspect-video bg-gray-100 animate-pulse" />
                    <div className="p-3 space-y-2">
                        <div className="h-3 bg-gray-100 rounded w-3/4 animate-pulse" />
                        <div className="h-3 bg-gray-100 rounded w-1/2 animate-pulse" />
                        <div className="h-3 bg-gray-100 rounded w-2/3 animate-pulse" />
                    </div>
                </div>
            ))}
        </div>
    );
}

// ─── EmptyState ────────────────────────────────────────────────────

function EmptyState({
    isFavOnly, hasFilters, onClear,
}: {
    isFavOnly: boolean;
    hasFilters: boolean;
    onClear: () => void;
}) {
    if (isFavOnly) {
        return (
            <div className="flex flex-col items-center justify-center py-16 text-center">
                <div className="w-14 h-14 rounded-full bg-amber-50 flex items-center justify-center mb-4">
                    <Star className="w-7 h-7 text-amber-400" />
                </div>
                <h3 className="text-base font-medium text-gray-900">
                    Você ainda não favoritou nenhum modelo
                </h3>
                <p className="text-sm text-gray-500 mt-1 mb-4 max-w-md">
                    Navegue pela biblioteca e clique na estrela dos modelos que quiser
                    reutilizar em Broadcasts ou Flows.
                </p>
                <Button variant="outline" size="sm" onClick={onClear}>
                    Ver todos os modelos
                </Button>
            </div>
        );
    }

    return (
        <div className="flex flex-col items-center justify-center py-16 text-center">
            <div className="w-14 h-14 rounded-full bg-gray-100 flex items-center justify-center mb-4">
                <AlertCircle className="w-7 h-7 text-gray-400" />
            </div>
            <h3 className="text-base font-medium text-gray-900">
                {hasFilters ? 'Nenhum modelo com esses filtros' : 'Nenhum modelo disponível'}
            </h3>
            <p className="text-sm text-gray-500 mt-1 mb-4 max-w-md">
                {hasFilters
                    ? 'Tente ajustar ou limpar os filtros pra ver mais resultados.'
                    : 'Os modelos vêm do catálogo do template-intelligence. Se você acabou de criar projetos, aguarde a indexação.'}
            </p>
            {hasFilters && (
                <Button variant="outline" size="sm" onClick={onClear}>
                    Limpar filtros
                </Button>
            )}
        </div>
    );
}

// ─── Helpers ───────────────────────────────────────────────────────

/** Conta (envios, cliques, leads): sem casas decimais quando < 1000. */
function formatCompact(n: number): string {
    if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
    if (n >= 1_000) return `${(n / 1_000).toFixed(1)}k`;
    return String(Math.round(n));
}

/** Dinheiro (revenue, eCPM, RPM): sempre 2 casas decimais. */
function formatMoney(n: number): string {
    if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(2)}M`;
    if (n >= 1_000) return `$${(n / 1_000).toFixed(2)}k`;
    return `$${n.toFixed(2)}`;
}
